// Family member list + invite — see docs/ONBOARDING.md §Step 2.
import { useEffect, useState } from "react";
import { api, type FamilyMember } from "../lib/api.js";
import { useFamily } from "../lib/family.js";
import { HeaderNav } from "../components/HeaderNav.js";
import { isValidPhone as validPhone, normalizePhone } from "../lib/phone.js";

interface GoogleStatus {
  connected: boolean;
  providerAccountId?: string | null;
}

interface PendingJoinRequest {
  id: string;
  requesterName: string;
  requesterEmail: string | null;
  requesterPhone: string | null;
  message: string | null;
  createdAt: string;
}

export function MembersPage() {
  const { family } = useFamily();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [inviteVia, setInviteVia] = useState<"sms" | "email">("sms");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-row pending action so we can disable the specific button being used.
  const [rowBusy, setRowBusy] = useState<{ id: string; kind: "resend" | "remove" } | null>(null);
  const [myGoogle, setMyGoogle] = useState<GoogleStatus | null>(null);
  const googleParam = new URLSearchParams(window.location.search).get("google");
  const [joinRequests, setJoinRequests] = useState<PendingJoinRequest[]>([]);
  const [reqBusy, setReqBusy] = useState<string | null>(null);

  useEffect(() => {
    if (family) void load();
  }, [family?.id]);

  // Pull pending join requests. Non-admins get 403 from the API — we just
  // swallow and leave the list empty so the section doesn't render for them.
  async function loadJoinRequests() {
    if (!family) return;
    try {
      const { requests } = await api.listJoinRequests(family.id);
      setJoinRequests(requests);
    } catch {
      setJoinRequests([]);
    }
  }
  useEffect(() => {
    if (family) void loadJoinRequests();
  }, [family?.id]);

  async function onApproveRequest(reqId: string) {
    if (!family) return;
    setReqBusy(reqId);
    setError(null);
    setStatus(null);
    try {
      await api.approveJoinRequest(family.id, reqId);
      setStatus("Request approved. They've been added to the family.");
      await Promise.all([load(), loadJoinRequests()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't approve");
    } finally {
      setReqBusy(null);
    }
  }

  async function onDenyRequest(reqId: string) {
    if (!family) return;
    if (!window.confirm("Deny this join request? They won't be added.")) return;
    setReqBusy(reqId);
    setError(null);
    setStatus(null);
    try {
      await api.denyJoinRequest(family.id, reqId);
      setStatus("Request denied.");
      await loadJoinRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't deny");
    } finally {
      setReqBusy(null);
    }
  }

  useEffect(() => {
    if (!family) return;
    void api
      .googleStatus("member")
      .then((s) =>
        setMyGoogle(
          s.connected
            ? { connected: true, providerAccountId: s.providerAccountId }
            : { connected: false },
        ),
      )
      .catch(() => setMyGoogle({ connected: false }));
  }, [family?.id]);

  async function load() {
    if (!family) return;
    const { members } = await api.listMembers(family.id);
    setMembers(members);
  }

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!family) return;
    if (inviteVia === "email" && !emailAddr.trim()) {
      setError("Email is required when sending an email invite.");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const res = await api.inviteMember(family.id, {
        phoneNumber: normalizePhone(phone),
        displayName: name.trim(),
        email: emailAddr.trim() || null,
        inviteVia,
      });
      const friendlyStatus: Record<string, string> = {
        invited: `SMS invite sent. ${name.trim()} will reply YES to confirm.`,
        email_invited: `Email invite sent to ${emailAddr.trim()}. ${name.trim()} has 7 days to accept.`,
        sms_outbound_unavailable:
          "Member added, but the SMS invite couldn't go out (messaging plan limit). Switch to email invite, or ask them to text the family number first.",
      };
      setStatus(
        friendlyStatus[res.status] ??
          `Member added (${res.status}). They can text the family number to confirm.`,
      );
      setPhone("");
      setName("");
      setEmailAddr("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add member");
    } finally {
      setBusy(false);
    }
  }

  async function onResend(member: FamilyMember) {
    if (!family) return;
    setRowBusy({ id: member.id, kind: "resend" });
    setError(null);
    setStatus(null);
    try {
      await api.resendInvite(family.id, member.id);
      setStatus(`Invite re-sent to ${member.displayName}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend invite");
    } finally {
      setRowBusy(null);
    }
  }

  async function onRemove(member: FamilyMember) {
    if (!family) return;
    const confirmed = window.confirm(
      `Remove ${member.displayName} from the family? They'll lose access immediately.`,
    );
    if (!confirmed) return;
    setRowBusy({ id: member.id, kind: "remove" });
    setError(null);
    setStatus(null);
    try {
      await api.removeMember(family.id, member.id);
      setStatus(`${member.displayName} removed.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove member");
    } finally {
      setRowBusy(null);
    }
  }

  if (!family) return <div className="p-8 text-stone-600">Loading…</div>;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">{family.name} family — members</h1>
          <HeaderNav active="members" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-6 space-y-6">
        {joinRequests.length > 0 && (
          <section className="bg-amber-50 rounded-xl border border-amber-200 p-6 shadow-sm">
            <h2 className="font-medium">
              Pending join requests
              <span className="ml-2 text-xs font-normal text-amber-700">
                ({joinRequests.length})
              </span>
            </h2>
            <ul className="mt-3 divide-y divide-amber-200">
              {joinRequests.map((r) => (
                <li key={r.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{r.requesterName}</p>
                    <p className="text-sm text-stone-600 truncate">
                      {[r.requesterEmail, r.requesterPhone].filter(Boolean).join(" · ") ||
                        "no contact"}
                    </p>
                    {r.message && (
                      <p className="mt-1 text-sm text-stone-700 italic">
                        "{r.message}"
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => void onApproveRequest(r.id)}
                      disabled={reqBusy !== null}
                      className="text-xs px-2 py-1 rounded-md bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-50"
                    >
                      {reqBusy === r.id ? "…" : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDenyRequest(r.id)}
                      disabled={reqBusy !== null}
                      className="text-xs px-2 py-1 rounded-md text-red-700 hover:bg-red-100 disabled:opacity-50"
                    >
                      Deny
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium">Your Google Calendar</h2>
          {googleParam === "connected" && (
            <div className="mt-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              ✓ Connected to Google. Your personal calendar will sync once event-sync ships.
            </div>
          )}
          {googleParam === "denied" && (
            <div className="mt-3 rounded-lg bg-stone-100 px-3 py-2 text-sm text-stone-700">
              No worries — you can connect later.
            </div>
          )}
          {googleParam === "upgrade_required" && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              Google Calendar sync is a paid feature — the family admin can upgrade from Settings.
            </div>
          )}
          {myGoogle === null ? (
            <p className="mt-3 text-sm text-stone-500">Loading…</p>
          ) : myGoogle.connected ? (
            <>
              <p className="mt-3 text-sm text-stone-700">
                Your personal Google Calendar is connected.
              </p>
              {myGoogle.providerAccountId && (
                <p className="mt-1 text-xs text-stone-500 break-all">
                  Linked Google account id: {myGoogle.providerAccountId}
                </p>
              )}
              <a
                href={api.googleStartUrl("member")}
                className="mt-4 inline-block btn-secondary"
              >
                Reconnect or switch account
              </a>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-stone-600">
                Optional: connect your own Google Calendar to keep your events in sync alongside
                the family calendar (event-sync ships next iteration).
              </p>
              <a
                href={api.googleStartUrl("member")}
                className="mt-4 inline-block btn-primary"
              >
                Connect my Google Calendar
              </a>
            </>
          )}
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium mb-3">Current members</h2>
          <ul className="divide-y divide-stone-100">
            {members.map((m) => {
              const isResending = rowBusy?.id === m.id && rowBusy.kind === "resend";
              const isRemoving = rowBusy?.id === m.id && rowBusy.kind === "remove";
              const rowDisabled = rowBusy !== null;
              return (
                <li key={m.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">
                      {m.displayName}{" "}
                      {m.role === "admin" && (
                        <span className="text-xs text-stone-500">(admin)</span>
                      )}
                    </p>
                    <p className="text-sm text-stone-500 truncate">{m.phoneNumber}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-xs rounded-full px-2 py-0.5 ${
                        m.authorizedForSms
                          ? "bg-green-100 text-green-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                      title={
                        m.authorizedForSms
                          ? "Confirmed — they can text events"
                          : `Awaiting their YES reply on the invite SMS`
                      }
                    >
                      {m.authorizedForSms ? "active" : "awaiting YES"}
                    </span>
                    {!m.authorizedForSms && (
                      <button
                        type="button"
                        onClick={() => void onResend(m)}
                        disabled={rowDisabled}
                        className="text-xs text-stone-700 hover:text-stone-900 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isResending ? "Sending…" : "Resend"}
                      </button>
                    )}
                    {m.role !== "admin" && (
                      <button
                        type="button"
                        onClick={() => void onRemove(m)}
                        disabled={rowDisabled}
                        aria-label={`Remove ${m.displayName}`}
                        className="text-xs text-stone-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRemoving ? "Removing…" : "Remove"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
            {members.length === 0 && (
              <li className="py-4 text-stone-600">
                No one else yet — invite a family member below to share this calendar.
              </li>
            )}
          </ul>
        </section>

        <section className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <h2 className="font-medium mb-3">Add someone</h2>
          <p className="text-sm text-stone-600 mb-4">
            {inviteVia === "sms"
              ? "They'll get an SMS asking them to reply YES. Once they do, they can text the family number any time to add events."
              : "They'll get an email with an accept link (good for 7 days). Once they accept, they can text the family number any time to add events. Phone number is still required so they can text in."}
          </p>
          <form onSubmit={onInvite} className="space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (e.g. Joshua)"
              required
              className="input"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 555 1234"
              required
              className="input"
            />
            {inviteVia === "email" && (
              <input
                type="email"
                value={emailAddr}
                onChange={(e) => setEmailAddr(e.target.value)}
                placeholder="them@example.com"
                required
                className="input"
              />
            )}
            <fieldset className="flex gap-4 pt-1">
              <legend className="sr-only">Send invite via</legend>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="radio"
                  name="inviteVia"
                  value="sms"
                  checked={inviteVia === "sms"}
                  onChange={() => setInviteVia("sms")}
                />
                Send via SMS
              </label>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input
                  type="radio"
                  name="inviteVia"
                  value="email"
                  checked={inviteVia === "email"}
                  onChange={() => setInviteVia("email")}
                />
                Send via email
              </label>
            </fieldset>
            <button
              type="submit"
              disabled={
                busy ||
                !validPhone(phone) ||
                (inviteVia === "email" && !emailAddr.trim())
              }
              className="btn-primary"
            >
              {busy ? "Sending…" : "Send invite"}
            </button>
            {status && <p className="text-sm text-stone-700">{status}</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </form>
        </section>
      </main>
    </div>
  );
}

// validPhone + normalizePhone moved to ../lib/phone (libphonenumber-js).
